import { PostalResponse } from '@dav-model/api/response/postal.response.model';
import { CartItem } from '@dav-model/cart/cart-item.model';
import { CartTaxFormValues } from '@dav-model/cart/cart-tax-form-values.model';
import { CheckoutFormValues } from '@dav-model/checkout/checkout-form-values.model';
import { SubmittedCartItem } from '@dav-model/checkout/submit_cart/response/submitted-cart-item.model';
import { CreditInsuranceCoverageOption } from '@dav-model/form/credit-insurance-coverage-option.model';
import { Forms } from '@dav-model/form/form';
import { OptionType } from '@dav-model/form/option-type.model';

import * as CartActions from './cart.actions';

export interface State {
  cart: CartItem[];
  validatedCart: CartItem[];
  cartTaxFormValues: CartTaxFormValues[];
  submittedCart: SubmittedCartItem[];
  checkoutForms: Forms;
  checkoutFormValues: CheckoutFormValues;
  total: number;
  error: string;
  success: boolean;
  savedQuote: boolean;
  submittedCartSuccess: boolean;
  addCartContainsError: boolean;
  cartCostContainsError: boolean;
  checkoutCartContainsError: boolean;
  customerPostal: PostalResponse;
  cosignerPostal: PostalResponse;
  cosignerFormEnable: boolean;
  exculdingCreditFormValid: boolean;
  creditFormEnable: boolean;
  isInValidRequestQuote: boolean;
  isValidSaveCredit: boolean;
  requestedQuote: any;
  loanProtectionCoverageOptions: OptionType[];
  creditInsurancePackageCoverageOptions: CreditInsuranceCoverageOption[];
}

const InitialState: State = {
  cart: [],
  validatedCart: [],
  submittedCart: [],
  cartTaxFormValues: [],
  checkoutForms: new Forms(),
  total: 0.00,
  error: '',
  success: false,
  savedQuote: false,
  submittedCartSuccess: false,
  addCartContainsError: false,
  cartCostContainsError: false,
  checkoutCartContainsError: false,
  customerPostal: new PostalResponse(),
  cosignerPostal: new PostalResponse(),
  checkoutFormValues: new CheckoutFormValues(),
  cosignerFormEnable: false,
  requestedQuote: {},
  creditFormEnable: false,
  exculdingCreditFormValid: false,
  isInValidRequestQuote: true,
  isValidSaveCredit: false,
  loanProtectionCoverageOptions: [
    {
      'value': '750',
      'description': 'coverageOptions.goldBasic',
      'checked': true
    },
    {
      'value': '751',
      'description': 'coverageOptions.goldEnhanced',
      'checked': true
    },
    {
      'value': '752',
      'description': 'coverageOptions.platinumBasic',
      'checked': true
    },
    {
      'value': '753',
      'description': 'coverageOptions.platinumEnhanced',
      'checked': true
    }
  ],
  creditInsurancePackageCoverageOptions: [
    {
      value: { single: '701', joint: '801' },
      description: 'coverageOptions.lifeDecreasingTerm',
      type: 'life'
    },
    {
      value: { single: '702', joint: '802' },
      description: 'coverageOptions.lifeDecreasingTermLevelTerm',
      type: 'life'
    },
    {
      value: { single: '721', joint: '821' },
      description: 'coverageOptions.criticalIllnessDecreasingTerm',
      type: 'criticalIllness'
    },
    {
      value: { single: '722', joint: '822' },
      description: 'coverageOptions.criticalIllnessDecreasingTermLevelTerm',
      type: 'criticalIllness'
    },
    {
      value: { single: '501', joint: '601' },
      description: 'coverageOptions.totalDisability30DayRetro',
      type: 'disability'
    },
    {
      value: { single: '511', joint: '611' },
      description: 'coverageOptions.totalDisability30DayElim',
      type: 'disability'
    },
    {
      value: { single: '513', joint: '613' },
      description: 'coverageOptions.totalDisability30DayElim12months',
      type: 'disability'
    },
  ]
};

export function reducer(state = InitialState, action: CartActions.All): State {
  switch (action.type) {

    case CartActions.ADD_CART: {
      return {
        ...state,
        // VERIFY - would be nice to find a better method to deep copy
        // Javascript's only method of doing a deep copy requires the parse and stringify
        cart: [...state.cart, JSON.parse(JSON.stringify(action.cartItem))],
        savedQuote: false
      };
    }

    case CartActions.DELETE_CART: {
      return {
        ...state,
        cart: state.cart.filter(item => item.key !== action.cartItemSelector.key),
        cartTaxFormValues: state.cartTaxFormValues.filter(item => item.key !== action.cartItemSelector.key),
        savedQuote: false
      };
    }

    case CartActions.UPDATE_CHECKOUT_FORM: {
      return {
        ...state,
        checkoutFormValues: {
          ...state.checkoutFormValues,
          ...action.payload
        }
      };
    }

    case CartActions.UPDATE_CUSTOMER_COST: {
      return {
        ...state,
        validatedCart: [
          ...state.validatedCart.map((elem: CartItem) =>
            elem.key === action.payload.cartItemKey
              ? {
                ...elem,
                product: {
                  ...elem.product,
                  customerCost: action.payload.customerCost
                }
              }
              : elem
          ),
        ]
      };
    }

    case CartActions.ADD_TAXES_SUCCESS: {
      return {
        ...state,
        validatedCart: action.payload
      };
    }

    case CartActions.UPDATE_TAX_FORM_VALUES: {
      return {
        ...state,
        cartTaxFormValues: action.payload
      };
    }


    case CartActions.GET_CHECKOUT_FORM_SUCCESS: {
      return {
        ...state,
        checkoutForms: action.payload,
        submittedCartSuccess: false
      };
    }

    case CartActions.SUBMIT_CART_SUCCESS: {

      return {
        ...state,
        cart: action.payload.invalidCart,
        validatedCart: action.payload.invalidCart,
        submittedCart: [...state.submittedCart, ...action.payload.submittedCart],
        savedQuote: false,
        submittedCartSuccess: action.payload.submittedCart.length > 0,
        checkoutCartContainsError: action.payload.invalidCart.length > 0
      };
    }

    case CartActions.TOGGLE_COSIGNER_FORM: {
      return {
        ...state,
        cosignerFormEnable: action.payload,
      };
    }

    case CartActions.GET_CUSTOMER_POSTAL_SUCCESS: {
      return {
        ...state,
        customerPostal: action.payload
      };
    }

    case CartActions.GET_COSIGNER_POSTAL_SUCCESS: {
      return {
        ...state,
        cosignerPostal: action.payload
      };
    }

    case CartActions.VALIDATE_CART_SUCCESS: {
      return {
        ...state,
        validatedCart: action.payload.validatedCart.validatedCart,
        success: true,
        addCartContainsError: action.payload.validatedCart.containsError,
        savedQuote: false
      };
    }

    case CartActions.LOAD_QUOTE_SUCCESS: {
      return {
        ...state,
        validatedCart: action.payload.cartState.validatedCart,
        cart: action.payload.cartState.cart,
        submittedCart: action.payload.cartState.submittedCart,
        total: action.payload.cartState.total,
        success: action.payload.cartState.success,
        error: action.payload.cartState.error,
        checkoutFormValues: action.payload.cartState.checkoutFormValues,
        customerPostal: action.payload.cartState.customerPostal,
        cosignerPostal: action.payload.cartState.cosignerPostal,
        cosignerFormEnable: action.payload.cartState.cosignerFormEnable,
        checkoutForms: action.payload.cartState.checkoutForms,
        submittedCartSuccess: false,
        addCartContainsError: action.payload.cartState.addCartContainsError,
        checkoutCartContainsError: false,
        savedQuote: true,
        requestedQuote: action.payload.cartState.requestedQuote,
        // use conditional operator for objects that may not be in load quote
        cartCostContainsError: action.payload.cartState['cartCostContainsError'] ?
          action.payload.cartState['cartCostContainsError'] : false,
        cartTaxFormValues: action.payload.cartState['cartTaxFormValues'] ?
          action.payload.cartState['cartTaxFormValues'] : []
      };
    }

    case CartActions.RESET_CART: {
      return {
        cart: [],
        validatedCart: [],
        submittedCart: [],
        cartTaxFormValues: [],
        checkoutForms: new Forms(),
        total: 0.00,
        error: '',
        success: false,
        savedQuote: false,
        submittedCartSuccess: false,
        addCartContainsError: false,
        cartCostContainsError: false,
        checkoutCartContainsError: false,
        customerPostal: new PostalResponse(),
        cosignerPostal: new PostalResponse(),
        checkoutFormValues: new CheckoutFormValues(),
        cosignerFormEnable: false,
        requestedQuote: [],
        creditFormEnable: false,
        exculdingCreditFormValid: false,
        isInValidRequestQuote: false,
        isValidSaveCredit: false,
        loanProtectionCoverageOptions: InitialState.loanProtectionCoverageOptions,
        creditInsurancePackageCoverageOptions: InitialState.creditInsurancePackageCoverageOptions
      };
    }

    case CartActions.CART_FAILURE: {
      return {
        ...state,
        success: false,
        error: action.payload,
        savedQuote: false
      };
    }

    case CartActions.SAVE_AS_UMU_QUOTE_SUCCESS: {
      return {
        ...state,
        requestedQuote: action.payload,
        isInValidRequestQuote: false
      };
    }


    case CartActions.SAVE_AS_UMU_QUOTE_FAILURE: {
      return {
        ...state,
        error: action.payload,
        isInValidRequestQuote: true
      };
    }

    case CartActions.SAVE_AS_UMU_CREDIT_SUCCESS: {

      return {
        ...state,
        isValidSaveCredit: true
      };
    }

    case CartActions.SAVE_AS_UMU_CREDIT_FAILURE: {
      return {
        ...state,
        isValidSaveCredit: false
      };
    }

    case CartActions.SET_CREDIT_FORM_ENABLE: {
      return {
        ...state,
        creditFormEnable: action.creditFormEnable
      };
    }

    case CartActions.RESET_INRESPONSE: {

      return {
        ...state,
        requestedQuote: {}
      };
    }

    default: {
      return {
        ...state
      };
    }
  }
}


